import { useCallback, useEffect, useRef, useState } from 'react'
import { transcribeAudio } from '@/lib/api'
import {
  assembleTranscript,
  friendlyTranscribeError,
  pickRecorderMimeType,
  type TranscriptSegment,
} from '@/lib/voiceNotes'

// Records the microphone in ~5-minute segments and transcribes each segment as
// soon as it ends, so a dropped connection or closed tab loses minutes, not the
// whole session. Each segment is a standalone file (the recorder is rotated,
// not time-sliced — a mid-stream webm/mp4 slice is not independently decodable).
// Hard cap 60 minutes. Recording only runs while the page is open and the
// screen is on — that is a PWA platform limit — so a screen Wake Lock is held
// for the duration where the browser supports it.

export const SEGMENT_MS = 5 * 60_000
export const MAX_DURATION_MS = 60 * 60_000

export type RecorderState = 'idle' | 'recording' | 'finishing'

interface UseVoiceRecorderOptions {
  /** Called once per recording with the assembled transcript ('' if silent). */
  onFinished: (transcript: string, durationSec: number) => void
}

export interface UseVoiceRecorderResult {
  state: RecorderState
  elapsedSec: number
  /** Segments uploaded and still transcribing. */
  transcribingCount: number
  /** Transcript assembled from the segments finished so far. */
  liveTranscript: string
  error: string | null
  isSupported: boolean
  start: () => Promise<void>
  stop: () => void
  cancel: () => void
  clearError: () => void
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '')
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

export function useVoiceRecorder({ onFinished }: UseVoiceRecorderOptions): UseVoiceRecorderResult {
  const [state, setState] = useState<RecorderState>('idle')
  const [elapsedSec, setElapsedSec] = useState(0)
  const [transcribingCount, setTranscribingCount] = useState(0)
  const [liveTranscript, setLiveTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)

  const isSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    pickRecorderMimeType() !== null

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const mimeTypeRef = useRef<string>('')
  const segmentsRef = useRef<TranscriptSegment[]>([])
  const uploadsRef = useRef<Promise<void>[]>([])
  const segmentIndexRef = useRef(0)
  const startedAtRef = useRef(0)
  // null = keep rolling into the next segment; 'stop' = finish + deliver;
  // 'cancel' = tear down and discard.
  const endModeRef = useRef<'stop' | 'cancel' | null>(null)
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const rotateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wakeLockRef = useRef<{ release: () => Promise<void> } | null>(null)
  const onFinishedRef = useRef(onFinished)
  onFinishedRef.current = onFinished

  const releaseWakeLock = useCallback(() => {
    wakeLockRef.current?.release().catch(() => {})
    wakeLockRef.current = null
  }, [])

  const requestWakeLock = useCallback(async () => {
    try {
      // Not supported everywhere — recording still works, the screen may just sleep.
      const nav = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } }
      wakeLockRef.current = (await nav.wakeLock?.request('screen')) ?? null
    } catch {
      wakeLockRef.current = null
    }
  }, [])

  const clearTimers = useCallback(() => {
    if (tickTimerRef.current) clearInterval(tickTimerRef.current)
    tickTimerRef.current = null
    if (rotateTimerRef.current) clearTimeout(rotateTimerRef.current)
    rotateTimerRef.current = null
  }, [])

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }, [])

  const resetToIdle = useCallback(() => {
    clearTimers()
    stopStream()
    releaseWakeLock()
    recorderRef.current = null
    setState('idle')
    setElapsedSec(0)
    setTranscribingCount(0)
  }, [clearTimers, stopStream, releaseWakeLock])

  const transcribeSegment = useCallback((blob: Blob, index: number) => {
    const run = async () => {
      try {
        const b64 = await blobToBase64(blob)
        let text: string
        try {
          text = (await transcribeAudio(b64, mimeTypeRef.current || blob.type || 'audio/webm')).text
        } catch {
          // One retry — a segment is minutes of conversation, worth a second attempt.
          text = (await transcribeAudio(b64, mimeTypeRef.current || blob.type || 'audio/webm')).text
        }
        segmentsRef.current[index] = { index, text }
      } catch (err) {
        segmentsRef.current[index] = { index, error: friendlyTranscribeError(err) }
      } finally {
        setLiveTranscript(assembleTranscript(segmentsRef.current.filter(Boolean)))
        setTranscribingCount((n) => Math.max(0, n - 1))
      }
    }
    setTranscribingCount((n) => n + 1)
    const p = run()
    uploadsRef.current.push(p)
  }, [])

  const finalize = useCallback(async () => {
    clearTimers()
    stopStream()
    releaseWakeLock()
    const durationSec = Math.round((Date.now() - startedAtRef.current) / 1000)
    // Wait for every segment upload — including the final one just enqueued.
    await Promise.allSettled(uploadsRef.current)
    const transcript = assembleTranscript(segmentsRef.current.filter(Boolean))
    recorderRef.current = null
    setState('idle')
    setElapsedSec(0)
    setTranscribingCount(0)
    setLiveTranscript('')
    onFinishedRef.current(transcript, durationSec)
  }, [clearTimers, stopStream, releaseWakeLock])

  const startSegmentRecorder = useCallback(() => {
    const stream = streamRef.current
    if (!stream) return
    const mimeType = mimeTypeRef.current
    const recorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType, audioBitsPerSecond: 32_000 } : { audioBitsPerSecond: 32_000 },
    )
    const chunks: BlobPart[] = []
    const segmentIndex = segmentIndexRef.current++

    recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) chunks.push(e.data)
    }
    recorder.onstop = () => {
      const mode = endModeRef.current
      if (mode !== 'cancel') {
        const blob = new Blob(chunks, { type: mimeType || 'audio/webm' })
        if (blob.size > 0) transcribeSegment(blob, segmentIndex)
      }
      if (mode === null) {
        // Segment rotation — keep recording on the same stream.
        startSegmentRecorder()
      } else if (mode === 'stop') {
        void finalize()
      }
      // mode === 'cancel': resetToIdle already ran.
    }
    recorder.start()
    recorderRef.current = recorder

    rotateTimerRef.current = setTimeout(() => {
      if (recorder.state !== 'inactive') recorder.stop()
    }, SEGMENT_MS)
  }, [transcribeSegment, finalize])

  const stop = useCallback(() => {
    if (state !== 'recording') return
    endModeRef.current = 'stop'
    setState('finishing')
    if (rotateTimerRef.current) clearTimeout(rotateTimerRef.current)
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') recorder.stop()
    else void finalize()
  }, [state, finalize])
  const stopRef = useRef(stop)
  stopRef.current = stop

  const start = useCallback(async () => {
    if (state !== 'idle') return
    setError(null)

    const mimeType = pickRecorderMimeType()
    if (!navigator.mediaDevices?.getUserMedia || mimeType === null) {
      setError('Voice recording is not supported in this browser.')
      return
    }

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      })
    } catch (err) {
      const name = err instanceof DOMException ? err.name : ''
      setError(
        name === 'NotAllowedError' || name === 'PermissionDeniedError'
          ? 'Microphone access was denied — allow it in your browser settings and try again.'
          : name === 'NotFoundError'
            ? 'No microphone was found on this device.'
            : 'Could not start the microphone.',
      )
      return
    }

    streamRef.current = stream
    mimeTypeRef.current = mimeType
    segmentsRef.current = []
    uploadsRef.current = []
    segmentIndexRef.current = 0
    startedAtRef.current = Date.now()
    endModeRef.current = null
    setLiveTranscript('')
    setElapsedSec(0)
    setState('recording')
    void requestWakeLock()

    tickTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current
      setElapsedSec(Math.floor(elapsed / 1000))
      if (elapsed >= MAX_DURATION_MS) stopRef.current()
    }, 500)

    startSegmentRecorder()
  }, [state, requestWakeLock, startSegmentRecorder])

  const cancel = useCallback(() => {
    if (state === 'idle') return
    endModeRef.current = 'cancel'
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') recorder.stop()
    segmentsRef.current = []
    uploadsRef.current = []
    setLiveTranscript('')
    resetToIdle()
  }, [state, resetToIdle])
  const cancelRef = useRef(cancel)
  cancelRef.current = cancel

  // Re-acquire the wake lock when the tab becomes visible again (the browser
  // releases it on hide), and tear the recorder down if the component unmounts.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && streamRef.current) void requestWakeLock()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      if (streamRef.current) cancelRef.current()
    }
  }, [requestWakeLock])

  const clearError = useCallback(() => setError(null), [])

  return {
    state,
    elapsedSec,
    transcribingCount,
    liveTranscript,
    error,
    isSupported,
    start,
    stop,
    cancel,
    clearError,
  }
}
